// Linked income uses the company's currency settings, even if a saved row has
// missing or outdated currency fields. Manual rows retain their own settings.
export const getForeignIncomeCurrencyDetails = (
  row,
  functionalCurrency = "SGD",
  functionalCurrencyRate = 1
) => ({
  currency: row.isPopulated
    ? functionalCurrency || "SGD"
    : row.functionalCurrency || "",
  exchangeRate: row.isPopulated
    ? functionalCurrencyRate
    : row.exchangeRate ?? "",
});

export const buildMainComputation = (
  foreignIncomeData,
  manualData,
  companyType = "common"
) => {
  // Trade / Branch Profits
  const tradeBranchRows = foreignIncomeData?.filter(
    (r) => r.incomeType === "Trade / Branch Profits" && r.isLocked
  );
  const grossTradeBranchProfits = tradeBranchRows?.reduce(
    (sum, r) => sum + r.grossIncome,
    0
  );

  // Other sourced income
  const otherIncomeRows = foreignIncomeData?.filter(
    (r) =>
      ["Interest", "Rental", "Royalty", "Dividend"].includes(r.incomeType) &&
      r.isLocked
  );
  const otherSourcedIncome = otherIncomeRows?.reduce(
    (sum, r) => sum + r.netIncome,
    0
  );

  // Statutory income = adjusted P/L + other sourced
  const statutoryIncome =
    manualData.statutoryIncome ||
    manualData.adjustedProfitLoss + otherSourcedIncome;

  // Full computed data
  const computedData = {
    grossTradeBranchProfits,
    totalRevenue: manualData.totalRevenue,
    adjustedProfitLoss: manualData.adjustedProfitLoss,
    otherSourcedIncome,
    statutoryIncome,
    taxPayable: manualData.taxPayable,
  };

  // Build rows
  const rows = [
    ...(tradeBranchRows?.map((r) => ({
      id: `trade-branch-${r.id}`,
      description: `${r.isPopulated ? r.source?.sch_name : r.scheduleName} (${
        r.incomeType
      })`,
      section: "A",
      value: r.grossIncome,
      isReadOnly: true,
      source: "foreign-income",
      country: r.country,
    })) || []),
    ...(tradeBranchRows?.length > 1
      ? [
          {
            id: "gross-trade-branch-total",
            description: "Total Gross Trade / Branch Profits",
            section: "A",
            value: grossTradeBranchProfits,
            isReadOnly: true,
            source: "foreign-income",
          },
        ]
      : []),
    ...(companyType === "common"
      ? [
          {
            id: "total-revenue",
            description: "Total Revenue",
            section: "B",
            value: manualData.totalRevenue,
            isReadOnly: false,
            source: "manual",
          },
        ]
      : []),
    ...(companyType === "common"
      ? [
          {
            id: "adjusted-profit-loss",
            description: "Adjusted Profit / (Loss) after Capital Allowances",
            section: "C",
            value: manualData.adjustedProfitLoss,
            isReadOnly: false,
            source: "manual",
          },
        ]
      : []),
    ...(otherIncomeRows?.map((r) => ({
      id: `foreign-income-${r.id}`,
      description: `${r.isPopulated ? r.source?.sch_name : r.scheduleName} (${
        r.incomeType
      })`,
      section: "D",
      value: r.netIncome,
      isReadOnly: true,
      source: "foreign-income",
      country: r.country,
    })) || []),
    // ...(otherIncomeRows?.length > 1
    //   ? [
    //       {
    //         id: "other-sourced-income-total",
    //         description: "Total Other Sourced Income",
    //         section: "D",
    //         value: otherSourcedIncome,
    //         isReadOnly: true,
    //         source: "foreign-income",
    //       },
    //     ]
    //   : []),
    {
      id: "statutory-income",
      description: "Statutory Income",
      info: "All sources of income after deducting expenses and capital allowances",
      section: "E",
      value: manualData.statutoryIncome,
      isReadOnly: false,
      source: "manual",
    },
    {
      id: "tax-payable",
      description: "Tax Payable",
      section: "F",
      value: manualData.taxPayable,
      isReadOnly: false,
      source: "manual",
    },
  ];

  return {
    computedData,
    rows,
    hasLockedData: foreignIncomeData?.some((r) => r.isLocked),
  };
};
